/**
 * Official draw history is durable storage for analytics / future ML.
 * UI live caps and pages only bound the feed. Manual delete of `results` is god-only.
 * There is no automatic purge.
 *
 * @see docs/lottery.md — Results history retention
 */

import type { ResultLike } from '../rules/officialDrawResults';

/** Live ResultsBoard / Hoy strip (realtime). History beyond this is paged. */
export const RESULT_HISTORY_LIVE_LIMIT = 24;

/** First unary page for Hoy (day + night, maybe yesterday if night is pending). */
export const RESULT_HISTORY_TODAY_LIMIT = 4;

/** @deprecated Use RESULT_HISTORY_LIVE_LIMIT — kept for draw-session comment parity. */
export const RESULT_HISTORY_SUBSCRIBE_LIMIT = RESULT_HISTORY_LIVE_LIMIT;

/** Live drawSessions feed (settlement/status; not the numbers corpus). */
export const DRAW_SESSION_SUBSCRIBE_LIMIT = 400;

/**
 * Anteriores first page (Cuba days). Further scroll pages use
 * {@link RESULT_HISTORY_PAGE_DAYS}. Esta semana is served by the live cap.
 */
export const RESULT_HISTORY_INITIAL_DAYS = 25;
export const RESULT_HISTORY_PAGE_DAYS = 10;

export interface ResultHistoryCursor {
  dayKey: string;
  window: 'day' | 'night';
  publishedAtMs?: number;
}

/** Day + night turns for a history page of `days` calendar rows. */
export function resultHistoryTurnLimit(days: number): number {
  return Math.max(1, Math.floor(days)) * 2;
}

/** Session cache key — week floor so Monday rollover starts a fresh Anteriores page. */
export function resultHistoryCacheKey(opts: {
  platform: boolean;
  source: string | null | undefined;
  houseId: string;
  weekFloorDayKey: string;
}): string {
  const kind = opts.platform ? 'platform' : 'house';
  return `${kind}:${opts.source ?? ''}:${opts.houseId}:${opts.weekFloorDayKey}`;
}

/**
 * `startAfter` this week's Monday (or Sunday-on-Monday) day turn so Anteriores
 * pages skip the live week. House `publishedAt` paging ignores dayKey.
 */
export function resultHistoryArchiveCursor(weekFloorDayKey: string): ResultHistoryCursor {
  return { dayKey: weekFloorDayKey, window: 'day' };
}

/** Drop this-week rows from a house Anteriores page (publishedAt query has no dayKey bound). */
export function excludeResultsOnOrAfterDay(
  rows: readonly ResultLike[],
  dayKey: string,
): ResultLike[] {
  const floor = String(dayKey ?? '').trim();
  if (!floor) return [...rows];
  return rows.filter((row) => String(row.dayKey ?? '') < floor);
}

export function resultHistoryTurnKey(row: ResultLike): string | null {
  const window = row.window === 'night' ? 'night' : row.window === 'day' ? 'day' : null;
  const dayKey = String(row.dayKey ?? '').trim();
  if (!window || !dayKey) return null;
  return `${dayKey}:${window}`;
}

/** First occurrence wins (pass live rows first so realtime updates stick). */
export function mergeResultLikes(pages: readonly ResultLike[][]): ResultLike[] {
  const byKey = new Map<string, ResultLike>();
  for (const page of pages) {
    for (const row of page) {
      const key = resultHistoryTurnKey(row);
      if (!key || byKey.has(key)) continue;
      byKey.set(key, row);
    }
  }
  return [...byKey.values()];
}

export function nextResultHistoryCursor(
  rows: readonly ResultLike[],
): ResultHistoryCursor | null {
  for (let i = rows.length - 1; i >= 0; i -= 1) {
    const row = rows[i];
    const window = row.window === 'night' ? 'night' : row.window === 'day' ? 'day' : null;
    const dayKey = String(row.dayKey ?? '').trim();
    if (!window || !dayKey) continue;
    const publishedAt = row.publishedAt;
    const publishedAtMs = typeof publishedAt === 'number'
      ? publishedAt
      : typeof publishedAt?.toMillis === 'function'
        ? publishedAt.toMillis()
        : typeof publishedAt?.seconds === 'number'
          ? publishedAt.seconds * 1000
          : undefined;
    return { dayKey, window, publishedAtMs };
  }
  return null;
}
